import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactLoading from 'react-loading';

import './specialOffers.css';

import { getSpecialOffer } from '../../api/infoAPI';
import TripCard from '../../components/tripCard/TripCard';
import formatedTripData from '../../components/tripCard/formatTripData/formatTripData';
import { fetchDataBaseOnLocation } from '../searchResult/searchResultFun/fetchFilters';

// ---------------------------------------------------------
// Helpers — all filtering is done here, the backend only
// sends the offer + its trips.
// ---------------------------------------------------------

const clean = (s) => String(s || '').trim();
const key = (s) => clean(s).toLowerCase();

// Start cities of a trip — price[].startCity, same field the search
// result page filters on (base_city → price.startCity)
const tripHasCity = (trip, city) =>
    Array.isArray(trip?.price) && trip.price.some((p) => key(p?.startCity) === key(city));

// Same price the card shows (ListingCard: actualPrice || finalDisplayPrice)
const getPrice = (trip) => {
    const f = formatedTripData(trip, 'trip');
    const p = f?.actualPrice || f?.finalDisplayPrice;
    return Number(p) > 0 ? Number(p) : Infinity;
};

const DURATIONS = [
    { value: 'all', label: 'Any duration' },
    { value: '1', label: '1 day', test: (d) => d <= 1 },
    { value: '2', label: '2 days', test: (d) => d === 2 },
    { value: '3', label: '3+ days', test: (d) => d >= 3 }
];

const SORTS = [
    { value: 'low', label: 'Price: Low to High' },
    { value: 'high', label: 'Price: High to Low' },
    { value: 'short', label: 'Duration: Shortest' }
];

const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

export default function SpecialOffers() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    // Start cities list — same one the search result page uses
    const [locationData, setLocationData] = useState([]);

    // Filters live in the URL so the page can be shared (?city=Bengaluru)
    const city = searchParams.get('city') || 'All';
    const duration = searchParams.get('days') || 'all';
    const sort = searchParams.get('sort') || 'low';

    const setParam = (name, value, defaultValue) => {
        const next = new URLSearchParams(searchParams);
        if (!value || value === defaultValue) next.delete(name);
        else next.set(name, value);
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        fetchDataBaseOnLocation('trip', (list) => setLocationData(Array.isArray(list) ? list : []));
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const res = await getSpecialOffer();
                const data = res?.data?.data;
                if (cancelled) return;
                if (!data) {
                    setOffer(null);
                    return;
                }
                // Remove duplicate trips
                const trips = Array.from(new Map((data.trips || []).map((t) => [t._id, t])).values());
                setOffer({ ...data, trips });
                if (data.name) document.title = `${data.name} | TrvTo`;
            } catch (e) {
                console.error('Failed to fetch special offer:', e);
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const trips = offer?.trips || [];

    // Start city chips: the search page's city list, keeping only cities
    // that have at least one trip in this offer (in the same order)
    const cities = useMemo(
        () =>
            locationData
                .map((c) => ({
                    name: c?.name,
                    label: c?.name,
                    count: trips.filter((t) => tripHasCity(t, c?.name)).length
                }))
                .filter((c) => c.name && c.count > 0),
        [locationData, trips]
    );

    // Only show duration chips that have trips
    const durations = useMemo(
        () => DURATIONS.filter((d) => d.value === 'all' || trips.some((t) => d.test(Number(t?.days) || 0))),
        [trips]
    );

    const visibleTrips = useMemo(() => {
        const dur = DURATIONS.find((d) => d.value === duration);
        const list = trips.filter((t) => {
            if (city !== 'All' && !tripHasCity(t, city)) return false;
            if (dur?.test && !dur.test(Number(t?.days) || 0)) return false;
            return true;
        });
        const withPrice = list.map((t) => ({ t, price: getPrice(t), days: Number(t?.days) || 0 }));
        withPrice.sort((a, b) => {
            if (sort === 'high') return (b.price === Infinity ? -1 : b.price) - (a.price === Infinity ? -1 : a.price);
            if (sort === 'short') return a.days - b.days || a.price - b.price;
            return a.price - b.price;
        });
        return withPrice.map((x) => x.t);
    }, [trips, city, duration, sort]);

    const hasFilters = city !== 'All' || duration !== 'all';
    const clearFilters = () => setSearchParams(sort !== 'low' ? { sort } : {}, { replace: true });

    // ------------------------------------------------------
    // States
    // ------------------------------------------------------

    if (loading) {
        return (
            <div className='loadingBackground'>
                <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
            </div>
        );
    }

    if (error || !offer || !trips.length) {
        return (
            <div className='sop-page'>
                <div className='sop-empty sop-empty-page'>
                    <h2>{error ? 'Could not load offers' : 'No special offer right now'}</h2>
                    <p>
                        {error
                            ? 'Please check your connection and try again.'
                            : 'Our next offer is coming soon. Meanwhile, explore all our trips.'}
                    </p>
                    <button type='button' className='sop-btn' onClick={() => navigate('/')}>
                        Explore trips
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className='sop-page'>
            {/* ── Banner ── */}
            {offer.bannerImage && (
                <div className='sop-banner'>
                    <img src={offer.bannerImage} alt={offer.name} />
                </div>
            )}

            {/* ── Heading ── */}
            <div className='sop-head'>
                <div>
                    <span className='sop-label'>SPECIAL OFFER</span>
                    <h1>{offer.name} Trips</h1>
                    <p>
                        {trips.length} handpicked trips at special prices
                        {offer.endDate ? ` · Offer valid till ${fmtDate(offer.endDate)}` : ''}
                    </p>
                </div>
            </div>

            {/* ── Filters ── */}
            <div className='sop-filters'>
                {cities.length > 0 && (
                    <div className='sop-filter-row'>
                        <span className='sop-filter-title'>Start city</span>
                        <div className='sop-chips'>
                            {[{ name: 'All', label: 'All', count: trips.length }, ...cities].map((c) => (
                                <button
                                    key={c.name}
                                    type='button'
                                    className={`sop-chip ${key(city) === key(c.name) ? 'active' : ''}`}
                                    onClick={() => setParam('city', c.name, 'All')}
                                >
                                    {c.label}
                                    <span>{c.count}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {durations.length > 2 && (
                    <div className='sop-filter-row'>
                        <span className='sop-filter-title'>Duration</span>
                        <div className='sop-chips'>
                            {durations.map((d) => (
                                <button
                                    key={d.value}
                                    type='button'
                                    className={`sop-chip ${duration === d.value ? 'active' : ''}`}
                                    onClick={() => setParam('days', d.value, 'all')}
                                >
                                    {d.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className='sop-toolbar'>
                    <span className='sop-count'>
                        Showing <b>{visibleTrips.length}</b> of {trips.length} trips
                        {hasFilters && (
                            <button type='button' className='sop-link' onClick={clearFilters}>
                                Clear filters
                            </button>
                        )}
                    </span>
                    <label className='sop-sort'>
                        Sort by
                        <select value={sort} onChange={(e) => setParam('sort', e.target.value, 'low')}>
                            {SORTS.map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>

            {/* ── Trips ── */}
            {visibleTrips.length ? (
                <div className='sop-grid'>
                    <TripCard imageData={visibleTrips} type='trip' />
                </div>
            ) : (
                <div className='sop-empty'>
                    <h3>No trips match these filters</h3>
                    <p>Try another start city or duration.</p>
                    <button type='button' className='sop-btn' onClick={clearFilters}>
                        Show all trips
                    </button>
                </div>
            )}
        </div>
    );
}
